/**
 * Canvas 2D side of the juice engine (the no-WebGL fallback and the dev gallery): draws the
 * same atlas frames with drawImage. White mask frames are tinted once per colour into a small
 * cached canvas (source-in fill), so the fallback looks like the Pixi path without per-frame
 * compositing tricks.
 */

var TINT_CACHE_MAX = 160;

/**
 * @param {{ pages: HTMLCanvasElement[], frames: Record<string, { page: number, x: number, y: number, w: number, h: number }> }} atlas
 */
export function createJuiceCanvasDrawer(atlas) {
  var tinted = new Map();
  var ctx = null;

  function tintedFrame(key, f, color) {
    var id = key + "|" + color;
    var cv = tinted.get(id);
    if (cv) return cv;
    cv = document.createElement("canvas");
    cv.width = f.w;
    cv.height = f.h;
    var c = cv.getContext("2d");
    c.drawImage(atlas.pages[f.page], f.x, f.y, f.w, f.h, 0, 0, f.w, f.h);
    c.globalCompositeOperation = "source-in";
    c.fillStyle = color;
    c.fillRect(0, 0, f.w, f.h);
    if (tinted.size >= TINT_CACHE_MAX) tinted.delete(tinted.keys().next().value);
    tinted.set(id, cv);
    return cv;
  }

  function blit(img, sx, sy, sw, sh, x, y, w, h, rot, alpha, blend) {
    var c = ctx;
    var flip = w < 0;
    var aw = Math.abs(w);
    c.globalAlpha = alpha;
    if (blend === "add") c.globalCompositeOperation = "lighter";
    if (rot || flip) {
      c.save();
      c.translate(x, y);
      if (rot) c.rotate(rot);
      if (flip) c.scale(-1, 1);
      c.drawImage(img, sx, sy, sw, sh, -aw / 2, -h / 2, aw, h);
      c.restore();
    } else {
      c.drawImage(img, sx, sy, sw, sh, x - aw / 2, y - h / 2, aw, h);
    }
    if (blend === "add") c.globalCompositeOperation = "source-over";
  }

  return {
    /** Bind the target context for this pass (its transform is the world's). */
    begin: function (targetCtx) {
      ctx = targetCtx;
      if (ctx) ctx.save();
    },
    /** @returns {boolean|undefined} false when the atlas has no such frame (the engine falls back) */
    frame: function (key, x, y, w, h, rot, alpha, tint, blend) {
      var f = atlas.frames[key];
      if (!f) return false;
      if (!ctx || !(alpha > 0.004) || !w || !h) return;
      if (tint) {
        blit(tintedFrame(key, f, tint), 0, 0, f.w, f.h, x, y, w, h, rot, alpha, blend);
      } else {
        blit(atlas.pages[f.page], f.x, f.y, f.w, f.h, x, y, w, h, rot, alpha, blend);
      }
    },
    canvas: function (canvas, x, y, w, h, rot, alpha) {
      if (!ctx || !canvas || !(alpha > 0.004) || !w || !h) return;
      blit(canvas, 0, 0, canvas.width, canvas.height, x, y, w, h, rot, alpha, null);
    },
    end: function () {
      if (ctx) ctx.restore();
      ctx = null;
    },
  };
}
